import React, { useState, useEffect } from 'react';
import { Handle, Position, useNodes } from '@xyflow/react';
import { 
  Card, 
  Select, 
  Input, 
  Button, 
  Space, 
  Typography, 
  Tabs, 
  Popconfirm, 
  Alert, 
  Spin, 
  message, 
  Switch, 
  Form, 
  InputNumber, 
  Tooltip, 
  Tag, 
  Collapse,
  Divider,
  Modal,
  Table
} from 'antd';
import { 
  DeleteOutlined, 
  ApiOutlined, 
  SettingOutlined, 
  PlusOutlined, 
  MinusCircleOutlined, 
  InfoCircleOutlined, 
  LockOutlined, 
  SendOutlined, 
  CopyOutlined, 
  ReloadOutlined,
  SlackOutlined,
  GithubOutlined,
  GoogleOutlined,
  MailOutlined,
  CalendarOutlined,
  DatabaseOutlined,
  CloudOutlined,
  BugOutlined,
  FileOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import GenericNodeContent from '../common/GenericNodeContent';
import NodeInputHandler from '../common/NodeInputHandler';
import axios from 'axios';
import config from '../../config';

const { Option, OptGroup } = Select;
const { Text, Title } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { TextArea } = Input;

// Helper functions for safe initialization
const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return [];
  return [];
};

const ensureObject = (value, defaultObj = {}) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  return defaultObj;
};

// Tool templates with common parameters
const TOOL_TEMPLATES = {
  // Slack templates
  send_message: [
    { name: 'channel', value: '', description: 'Channel ID or name (e.g., #general)', required: true },
    { name: 'text', value: '', description: 'Message text content', required: true },
    { name: 'thread_ts', value: '', description: 'Thread timestamp (optional)', required: false }
  ],
  list_channels: [
    { name: 'types', value: 'public_channel,private_channel', description: 'Channel types to include', required: false },
    { name: 'limit', value: '100', description: 'Maximum number of channels', required: false }
  ],
  get_messages: [
    { name: 'channel', value: '', description: 'Channel ID', required: true },
    { name: 'limit', value: '10', description: 'Number of messages to retrieve', required: false }
  ],
  create_channel: [
    { name: 'name', value: '', description: 'Channel name (lowercase, no spaces)', required: true },
    { name: 'is_private', value: 'false', description: 'Create private channel', required: false }
  ],

  // GitHub templates
  create_issue: [
    { name: 'repo', value: '', description: 'Repository name (owner/repo)', required: true },
    { name: 'title', value: '', description: 'Issue title', required: true },
    { name: 'body', value: '', description: 'Issue description', required: false },
    { name: 'labels', value: '', description: 'Comma-separated labels', required: false }
  ],
  list_repos: [
    { name: 'type', value: 'all', description: 'Repository type (all, owner, public, private)', required: false },
    { name: 'sort', value: 'updated', description: 'Sort by (created, updated, pushed, full_name)', required: false }
  ],
  create_pr: [
    { name: 'repo', value: '', description: 'Repository name (owner/repo)', required: true },
    { name: 'title', value: '', description: 'Pull request title', required: true },
    { name: 'head', value: '', description: 'Source branch', required: true },
    { name: 'base', value: 'main', description: 'Target branch', required: true },
    { name: 'body', value: '', description: 'Pull request description', required: false }
  ],
  get_file_content: [
    { name: 'repo', value: '', description: 'Repository name (owner/repo)', required: true },
    { name: 'path', value: '', description: 'File path', required: true },
    { name: 'ref', value: 'main', description: 'Branch or commit', required: false }
  ],

  // Notion templates
  create_page: [
    { name: 'parent_id', value: '', description: 'Parent page or database ID', required: true },
    { name: 'title', value: '', description: 'Page title', required: true },
    { name: 'content', value: '', description: 'Page content (markdown)', required: false }
  ],
  update_database: [
    { name: 'database_id', value: '', description: 'Database ID', required: true },
    { name: 'properties', value: '{}', description: 'Properties as JSON object', required: true }
  ],
  search_pages: [
    { name: 'query', value: '', description: 'Search query', required: false },
    { name: 'filter', value: '{}', description: 'Filter criteria as JSON', required: false }
  ],

  // Google Drive templates
  upload_file: [
    { name: 'name', value: '', description: 'File name', required: true },
    { name: 'content', value: '', description: 'File content or path', required: true },
    { name: 'parent_folder_id', value: '', description: 'Parent folder ID (optional)', required: false }
  ],
  search_files: [
    { name: 'query', value: '', description: 'Search query', required: false },
    { name: 'mime_type', value: '', description: 'MIME type filter', required: false }
  ],
  create_folder: [
    { name: 'name', value: '', description: 'Folder name', required: true },
    { name: 'parent_folder_id', value: '', description: 'Parent folder ID (optional)', required: false }
  ],

  // Google Calendar templates
  create_event: [
    { name: 'summary', value: '', description: 'Event title', required: true },
    { name: 'start_time', value: '', description: 'Start time (ISO format)', required: true },
    { name: 'end_time', value: '', description: 'End time (ISO format)', required: true },
    { name: 'description', value: '', description: 'Event description', required: false },
    { name: 'attendees', value: '', description: 'Comma-separated email addresses', required: false }
  ],
  list_events: [
    { name: 'calendar_id', value: 'primary', description: 'Calendar ID', required: false },
    { name: 'time_min', value: '', description: 'Start time filter (ISO)', required: false },
    { name: 'max_results', value: '10', description: 'Maximum number of events', required: false }
  ],

  // Gmail templates
  send_email: [
    { name: 'to', value: '', description: 'Recipient email address', required: true },
    { name: 'subject', value: '', description: 'Email subject', required: true },
    { name: 'body', value: '', description: 'Email body (HTML or plain text)', required: true },
    { name: 'cc', value: '', description: 'CC recipients (comma-separated)', required: false }
  ],
  search_emails: [
    { name: 'query', value: '', description: 'Gmail search query', required: true },
    { name: 'max_results', value: '10', description: 'Maximum number of results', required: false }
  ],

  // PostgreSQL templates
  query: [
    { name: 'sql', value: '', description: 'SQL query to execute', required: true },
    { name: 'params', value: '[]', description: 'Query parameters as JSON array', required: false }
  ],
  insert: [
    { name: 'table', value: '', description: 'Table name', required: true },
    { name: 'data', value: '{}', description: 'Data to insert as JSON object', required: true }
  ],

  // Chroma templates
  add_documents: [
    { name: 'collection_name', value: '', description: 'Collection name', required: true },
    { name: 'documents', value: '[]', description: 'Array of documents to add', required: true },
    { name: 'metadatas', value: '[]', description: 'Array of metadata objects', required: false }
  ],
  query_collection: [
    { name: 'collection_name', value: '', description: 'Collection name', required: true },
    { name: 'query_texts', value: '[]', description: 'Array of query strings', required: true },
    { name: 'n_results', value: '5', description: 'Number of results to return', required: false }
  ]
};

// Preset MCP server configurations
const PRESET_SERVERS = {
  slack: {
    name: 'Slack',
    description: 'Send messages, manage channels, and interact with Slack workspaces',
    icon: <SlackOutlined />,
    authFields: [
      { key: 'bot_token', label: 'Bot Token', type: 'password', required: true, placeholder: 'xoxb-...' },
      { key: 'workspace_url', label: 'Workspace URL', type: 'text', required: false, placeholder: 'https://yourworkspace.slack.com' }
    ],
    tools: [
      { id: 'send_message', name: 'Send Message', description: 'Send a message to a channel or user' },
      { id: 'list_channels', name: 'List Channels', description: 'Get list of channels in workspace' },
      { id: 'get_messages', name: 'Get Messages', description: 'Retrieve messages from a channel' },
      { id: 'create_channel', name: 'Create Channel', description: 'Create a new channel' },
      { id: 'invite_user', name: 'Invite User', description: 'Invite user to channel' }
    ]
  },
  github: {
    name: 'GitHub',
    description: 'Manage repositories, issues, pull requests, and GitHub workflows',
    icon: <GithubOutlined />,
    authFields: [
      { key: 'access_token', label: 'Personal Access Token', type: 'password', required: true, placeholder: 'ghp_...' },
      { key: 'organization', label: 'Organization (Optional)', type: 'text', required: false, placeholder: 'your-org' }
    ],
    tools: [
      { id: 'create_issue', name: 'Create Issue', description: 'Create a new issue in a repository' },
      { id: 'list_repos', name: 'List Repositories', description: 'Get list of repositories' },
      { id: 'create_pr', name: 'Create Pull Request', description: 'Create a new pull request' },
      { id: 'get_file_content', name: 'Get File Content', description: 'Retrieve content of a file' },
      { id: 'commit_file', name: 'Commit File', description: 'Create or update a file' }
    ]
  },
  notion: {
    name: 'Notion',
    description: 'Create pages, update databases, and manage Notion workspaces',
    icon: <FileOutlined />,
    authFields: [
      { key: 'integration_token', label: 'Integration Token', type: 'password', required: true, placeholder: 'secret_...' }
    ],
    tools: [
      { id: 'create_page', name: 'Create Page', description: 'Create a new page' },
      { id: 'update_database', name: 'Update Database', description: 'Add or update database entries' },
      { id: 'search_pages', name: 'Search Pages', description: 'Search for pages and databases' },
      { id: 'add_block', name: 'Add Block', description: 'Add content blocks to a page' }
    ]
  },
  google_drive: {
    name: 'Google Drive',
    description: 'Upload files, create folders, and manage Google Drive storage',
    icon: <GoogleOutlined />,
    authFields: [
      { key: 'access_token', label: 'Access Token', type: 'password', required: true },
      { key: 'refresh_token', label: 'Refresh Token', type: 'password', required: false }
    ],
    tools: [
      { id: 'upload_file', name: 'Upload File', description: 'Upload a file to Drive' },
      { id: 'search_files', name: 'Search Files', description: 'Search for files and folders' },
      { id: 'create_folder', name: 'Create Folder', description: 'Create a new folder' },
      { id: 'share_file', name: 'Share File', description: 'Share a file with others' }
    ]
  },
  google_calendar: {
    name: 'Google Calendar',
    description: 'Create events, manage calendars, and schedule meetings',
    icon: <CalendarOutlined />,
    authFields: [
      { key: 'access_token', label: 'Access Token', type: 'password', required: true },
      { key: 'refresh_token', label: 'Refresh Token', type: 'password', required: false }
    ],
    tools: [
      { id: 'create_event', name: 'Create Event', description: 'Create a new calendar event' },
      { id: 'list_events', name: 'List Events', description: 'Get upcoming events' },
      { id: 'find_free_slots', name: 'Find Free Slots', description: 'Find available time slots' },
      { id: 'update_event', name: 'Update Event', description: 'Update an existing event' }
    ]
  },
  gmail: {
    name: 'Gmail',
    description: 'Send emails, search messages, and manage Gmail',
    icon: <MailOutlined />,
    authFields: [
      { key: 'access_token', label: 'Access Token', type: 'password', required: true },
      { key: 'refresh_token', label: 'Refresh Token', type: 'password', required: false }
    ],
    tools: [
      { id: 'send_email', name: 'Send Email', description: 'Send an email message' },
      { id: 'search_emails', name: 'Search Emails', description: 'Search for email messages' },
      { id: 'create_draft', name: 'Create Draft', description: 'Create a draft email' },
      { id: 'add_label', name: 'Add Label', description: 'Add label to messages' }
    ]
  },
  postgresql: {
    name: 'PostgreSQL',
    description: 'Execute queries and manage PostgreSQL databases',
    icon: <DatabaseOutlined />,
    authFields: [
      { key: 'host', label: 'Host', type: 'text', required: true, placeholder: 'localhost' },
      { key: 'port', label: 'Port', type: 'number', required: true, placeholder: '5432' },
      { key: 'database', label: 'Database', type: 'text', required: true },
      { key: 'username', label: 'Username', type: 'text', required: true },
      { key: 'password', label: 'Password', type: 'password', required: true }
    ],
    tools: [
      { id: 'query', name: 'Execute Query', description: 'Execute a SQL query' },
      { id: 'insert', name: 'Insert Record', description: 'Insert a new record' },
      { id: 'update', name: 'Update Record', description: 'Update existing records' },
      { id: 'create_table', name: 'Create Table', description: 'Create a new table' }
    ]
  },
  chroma: {
    name: 'Chroma',
    description: 'Manage vector databases and embeddings with Chroma',
    icon: <CloudOutlined />,
    authFields: [
      { key: 'host', label: 'Host', type: 'text', required: true, placeholder: 'localhost' },
      { key: 'port', label: 'Port', type: 'number', required: true, placeholder: '8000' },
      { key: 'api_key', label: 'API Key (Optional)', type: 'password', required: false }
    ],
    tools: [
      { id: 'add_documents', name: 'Add Documents', description: 'Add documents to a collection' },
      { id: 'query_collection', name: 'Query Collection', description: 'Search for similar documents' },
      { id: 'create_collection', name: 'Create Collection', description: 'Create a new collection' },
      { id: 'delete_documents', name: 'Delete Documents', description: 'Remove documents from collection' }
    ]
  }
};

const MCPClientNode = ({ id, data, isConnectable }) => {
  // State management with defensive initialization
  const [nodeName, setNodeName] = useState(data.nodeName || `MCP Client ${id}`);
  const [serverType, setServerType] = useState(data.serverType || 'slack');
  const [connectionConfig, setConnectionConfig] = useState(ensureObject(data.connectionConfig, {}));
  const [selectedTool, setSelectedTool] = useState(data.selectedTool || '');
  const [toolParameters, setToolParameters] = useState(ensureArray(data.toolParameters));
  const [customServerConfig, setCustomServerConfig] = useState(ensureObject(data.customServerConfig, {
    name: '',
    description: '',
    connectionType: 'http',
    endpoint: '',
    command: '',
    args: [],
    authFields: [],
    customTools: []
  }));
  
  // UI state
  const [activeTab, setActiveTab] = useState('server');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [discoveredTools, setDiscoveredTools] = useState([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [toolsDiscovered, setToolsDiscovered] = useState(false);
  const [helpModalVisible, setHelpModalVisible] = useState(false);

  const nodes = useNodes();

  // Update node data when state changes
  useEffect(() => {
    data.updateNodeData(id, {
      nodeName,
      serverType,
      connectionConfig,
      selectedTool,
      toolParameters,
      customServerConfig,
      discoveredTools,
      testResult
    });
  }, [nodeName, serverType, connectionConfig, selectedTool, toolParameters, customServerConfig, discoveredTools, testResult, id, data]);

  // Get current server configuration
  const getCurrentServer = () => {
    if (serverType === 'custom') {
      return {
        name: customServerConfig.name || 'Custom Server',
        description: customServerConfig.description || 'Custom MCP Server',
        icon: <ApiOutlined />,
        authFields: customServerConfig.authFields || [],
        tools: toolsDiscovered ? discoveredTools : (customServerConfig.customTools || [])
      };
    }
    return PRESET_SERVERS[serverType] || PRESET_SERVERS.slack;
  };

  const currentServer = getCurrentServer();

  // Handle server type change
  const handleServerTypeChange = (value) => {
    setServerType(value);
    setSelectedTool('');
    setToolParameters([]);
    setConnectionConfig({});
    setToolsDiscovered(false);
    setDiscoveredTools([]);
    setTestResult(null);
    setTestError(null);
  };

  // Handle connection config changes
  const handleConnectionConfigChange = (key, value) => {
    setConnectionConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle custom server config changes
  const handleCustomServerConfigChange = (key, value) => {
    setCustomServerConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Add auth field for custom server
  const handleAddAuthField = () => {
    const newAuthFields = [...(customServerConfig.authFields || [])];
    newAuthFields.push({ key: '', label: '', type: 'text', required: true, placeholder: '' });
    setCustomServerConfig(prev => ({
      ...prev,
      authFields: newAuthFields
    }));
  };

  // Update auth field for custom server
  const handleAuthFieldChange = (index, field, value) => {
    const newAuthFields = [...(customServerConfig.authFields || [])];
    newAuthFields[index] = { ...newAuthFields[index], [field]: value };
    setCustomServerConfig(prev => ({
      ...prev,
      authFields: newAuthFields
    }));
  };

  // Remove auth field for custom server
  const handleRemoveAuthField = (index) => {
    const newAuthFields = (customServerConfig.authFields || []).filter((_, i) => i !== index);
    setCustomServerConfig(prev => ({
      ...prev,
      authFields: newAuthFields
    }));
  };

  // Add custom tool
  const handleAddCustomTool = () => {
    const newTools = [...(customServerConfig.customTools || [])];
    newTools.push({ id: '', name: '', description: '' });
    setCustomServerConfig(prev => ({
      ...prev,
      customTools: newTools
    }));
  };

  // Update custom tool
  const handleCustomToolChange = (index, field, value) => {
    const newTools = [...(customServerConfig.customTools || [])];
    newTools[index] = { ...newTools[index], [field]: value };
    setCustomServerConfig(prev => ({
      ...prev,
      customTools: newTools
    }));
  };

  // Remove custom tool
  const handleRemoveCustomTool = (index) => {
    const newTools = (customServerConfig.customTools || []).filter((_, i) => i !== index);
    setCustomServerConfig(prev => ({
      ...prev,
      customTools: newTools
    }));
  };

  // Handle tool parameter changes
  const handleToolParameterChange = (index, field, value) => {
    const newParams = [...toolParameters];
    newParams[index] = { ...newParams[index], [field]: value };
    setToolParameters(newParams);
  };

  // Add new tool parameter
  const handleAddToolParameter = () => {
    setToolParameters([...toolParameters, { name: '', value: '', description: '', required: false }]);
  };

  // Load template for selected tool
  const loadToolTemplate = () => {
    if (selectedTool && TOOL_TEMPLATES[selectedTool]) {
      setToolParameters([...TOOL_TEMPLATES[selectedTool]]);
      message.success('Template loaded successfully');
    } else {
      message.warning('No template available for this tool');
    }
  };

  // Remove tool parameter
  const handleRemoveToolParameter = (index) => {
    setToolParameters(toolParameters.filter((_, i) => i !== index));
  };

  // Discover tools for the selected server
  const discoverTools = async () => {
    if (data.isViewer) return;

    setLoadingTools(true);
    try {
      const serverConfig = {
        serverType,
        connectionConfig,
        customServerConfig: serverType === 'custom' ? customServerConfig : null
      };

      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/mcp-tools/${serverType}`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce,
          'Content-Type': 'application/json'
        },
        params: { config: JSON.stringify(serverConfig) }
      });

      if (response.data && Array.isArray(response.data)) {
        setDiscoveredTools(response.data);
        setToolsDiscovered(true);
        message.success('Tools discovered successfully');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error discovering tools:', error);
      message.error('Failed to discover tools: ' + (error.response?.data?.message || error.message));
      // Fallback to preset tools for known servers
      if (PRESET_SERVERS[serverType]) {
        setDiscoveredTools(PRESET_SERVERS[serverType].tools);
        setToolsDiscovered(true);
      }
    } finally {
      setLoadingTools(false);
    }
  };

  // Test MCP connection and tool execution
  const testConnection = async () => {
    if (data.isViewer) return;

    setIsTesting(true);
    setTestError(null);
    setTestResult(null);

    try {
      const testConfig = {
        serverType,
        connectionConfig,
        customServerConfig: serverType === 'custom' ? customServerConfig : null,
        selectedTool,
        toolParameters: toolParameters.reduce((acc, param) => {
          if (param.name && param.value) {
            acc[param.name] = param.value;
          }
          return acc;
        }, {})
      };

      const response = await axios.post(`${config.apiSettings.root}wp-ai-workflows/v1/mcp-test`, testConfig, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce,
          'Content-Type': 'application/json'
        }
      });

      setTestResult(response.data);
      setActiveTab('test');
      message.success('Test completed successfully');
    } catch (error) {
      console.error('Error testing MCP connection:', error);
      setTestError(error.response?.data?.message || error.message);
      setActiveTab('test');
    } finally {
      setIsTesting(false);
    }
  };

  // Render authentication fields
  const renderAuthFields = () => {
    const authFields = currentServer.authFields || [];
    
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        {authFields.map((field, index) => (
          <div key={field.key} style={{ marginBottom: 16 }}>
            <Text strong>
              {field.label}
              {field.required && <Text type="danger"> *</Text>}
            </Text>
            {field.type === 'password' ? (
              <Input.Password
                placeholder={field.placeholder}
                value={connectionConfig[field.key] || ''}
                onChange={(e) => handleConnectionConfigChange(field.key, e.target.value)}
                className="nodrag"
                style={{ marginTop: 4 }}
              />
            ) : field.type === 'number' ? (
              <InputNumber
                placeholder={field.placeholder}
                value={connectionConfig[field.key] || ''}
                onChange={(value) => handleConnectionConfigChange(field.key, value)}
                className="nodrag"
                style={{ width: '100%', marginTop: 4 }}
              />
            ) : (
              <Input
                placeholder={field.placeholder}
                value={connectionConfig[field.key] || ''}
                onChange={(e) => handleConnectionConfigChange(field.key, e.target.value)}
                className="nodrag"
                style={{ marginTop: 4 }}
              />
            )}
          </div>
        ))}

        {authFields.length === 0 && (
          <Alert
            type="info"
            message="No authentication required"
            description="This server type doesn't require authentication credentials."
          />
        )}
      </Space>
    );
  };

  // Render custom server configuration
  const renderCustomServerConfig = () => {
    if (serverType !== 'custom') return null;

    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Text strong>Server Details</Text>
        
        <Input
          placeholder="Server Name"
          value={customServerConfig.name}
          onChange={(e) => handleCustomServerConfigChange('name', e.target.value)}
          className="nodrag"
        />
        
        <TextArea
          placeholder="Description"
          value={customServerConfig.description}
          onChange={(e) => handleCustomServerConfigChange('description', e.target.value)}
          className="nodrag"
          rows={2}
        />

        <Text strong>Connection Type</Text>
        <Select
          value={customServerConfig.connectionType}
          onChange={(value) => handleCustomServerConfigChange('connectionType', value)}
          style={{ width: '100%' }}
          className="nodrag"
        >
          <Option value="http">HTTP/HTTPS</Option>
          <Option value="stdio">Local Process (stdio)</Option>
        </Select>

        {customServerConfig.connectionType === 'http' ? (
          <Input
            placeholder="Server Endpoint (e.g., http://localhost:8000)"
            value={customServerConfig.endpoint}
            onChange={(e) => handleCustomServerConfigChange('endpoint', e.target.value)}
            className="nodrag"
          />
        ) : (
          <>
            <Input
              placeholder="Command (e.g., python)"
              value={customServerConfig.command}
              onChange={(e) => handleCustomServerConfigChange('command', e.target.value)}
              className="nodrag"
            />
            <TextArea
              placeholder="Arguments (one per line)"
              value={Array.isArray(customServerConfig.args) ? customServerConfig.args.join('\n') : ''}
              onChange={(e) => handleCustomServerConfigChange('args', e.target.value.split('\n').filter(arg => arg.trim()))}
              className="nodrag"
              rows={3}
            />
          </>
        )}

        <Divider />
        
        <Text strong>Authentication Fields</Text>
        {(customServerConfig.authFields || []).map((field, index) => (
          <div key={index} style={{ 
            padding: '8px',
            background: '#f5f5f5',
            borderRadius: '6px',
            marginBottom: 8
          }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: 8 }}>
              <Input
                placeholder="Field Key (e.g., api_key)"
                value={field.key || ''}
                onChange={(e) => handleAuthFieldChange(index, 'key', e.target.value)}
                style={{ flex: 1 }}
                className="nodrag"
              />
              <Button 
                icon={<MinusCircleOutlined />}
                onClick={() => handleRemoveAuthField(index)}
                danger
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: 8 }}>
              <Input
                placeholder="Label (e.g., API Key)"
                value={field.label || ''}
                onChange={(e) => handleAuthFieldChange(index, 'label', e.target.value)}
                style={{ flex: 2 }}
                className="nodrag"
              />
              <Select
                value={field.type || 'text'}
                onChange={(value) => handleAuthFieldChange(index, 'type', value)}
                style={{ flex: 1 }}
                className="nodrag"
              >
                <Option value="text">Text</Option>
                <Option value="password">Password</Option>
                <Option value="number">Number</Option>
              </Select>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                placeholder="Placeholder text"
                value={field.placeholder || ''}
                onChange={(e) => handleAuthFieldChange(index, 'placeholder', e.target.value)}
                style={{ flex: 2 }}
                className="nodrag"
              />
              <Switch
                checked={field.required !== false}
                onChange={(checked) => handleAuthFieldChange(index, 'required', checked)}
                className="nodrag"
              />
              <Text style={{ lineHeight: '32px' }}>Required</Text>
            </div>
          </div>
        ))}
        <Button 
          type="dashed"
          onClick={handleAddAuthField}
          icon={<PlusOutlined />}
          style={{ width: '100%' }}
        >
          Add Auth Field
        </Button>

        <Divider />

        <Text strong>Custom Tools</Text>
        {(customServerConfig.customTools || []).map((tool, index) => (
          <div key={index} style={{ 
            padding: '8px',
            background: '#f5f5f5',
            borderRadius: '6px',
            marginBottom: 8
          }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: 8 }}>
              <Input
                placeholder="Tool ID (e.g., send_message)"
                value={tool.id || ''}
                onChange={(e) => handleCustomToolChange(index, 'id', e.target.value)}
                style={{ flex: 1 }}
                className="nodrag"
              />
              <Button 
                icon={<MinusCircleOutlined />}
                onClick={() => handleRemoveCustomTool(index)}
                danger
              />
            </div>
            <Input
              placeholder="Tool Name (e.g., Send Message)"
              value={tool.name || ''}
              onChange={(e) => handleCustomToolChange(index, 'name', e.target.value)}
              style={{ marginBottom: 8 }}
              className="nodrag"
            />
            <TextArea
              placeholder="Tool Description"
              value={tool.description || ''}
              onChange={(e) => handleCustomToolChange(index, 'description', e.target.value)}
              rows={2}
              className="nodrag"
            />
          </div>
        ))}
        <Button 
          type="dashed"
          onClick={handleAddCustomTool}
          icon={<PlusOutlined />}
          style={{ width: '100%' }}
        >
          Add Custom Tool
        </Button>
      </Space>
    );
  };

  // Render tool configuration
  const renderToolConfiguration = () => {
    const availableTools = toolsDiscovered ? discoveredTools : currentServer.tools;

    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>Available Tools</Text>
          <Space>
            {!toolsDiscovered && (
              <Button
                icon={<ReloadOutlined />}
                onClick={discoverTools}
                loading={loadingTools}
                size="small"
                type="link"
              >
                Discover
              </Button>
            )}
            <Tag color={toolsDiscovered ? 'green' : 'blue'}>
              {toolsDiscovered ? 'Live' : 'Preset'}
            </Tag>
          </Space>
        </div>

        <Select
          placeholder="Select a tool to execute"
          value={selectedTool}
          onChange={(value) => {
            setSelectedTool(value);
            setToolParameters([]);
          }}
          style={{ width: '100%' }}
          className="nodrag"
          loading={loadingTools}
        >
          {availableTools.map(tool => (
            <Option key={tool.id} value={tool.id}>
              <Space>
                <Text strong>{tool.name}</Text>
                <Text type="secondary">- {tool.description}</Text>
                {TOOL_TEMPLATES[tool.id] && (
                  <Tag color="orange" size="small">Template</Tag>
                )}
              </Space>
            </Option>
          ))}
        </Select>

        {selectedTool && (
          <>
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Text strong>Tool Parameters</Text>
              <Space>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {toolParameters.length} parameter{toolParameters.length !== 1 ? 's' : ''}
                </Text>
                {TOOL_TEMPLATES[selectedTool] && (
                  <Button
                    icon={<ThunderboltOutlined />}
                    onClick={loadToolTemplate}
                    type="primary"
                    size="small"
                  >
                    Load Template
                  </Button>
                )}
              </Space>
            </div>
            
            {toolParameters.map((param, index) => (
              <div key={index} style={{ 
                padding: '12px',
                background: '#f5f5f5',
                borderRadius: '6px',
                marginBottom: 8
              }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: 8 }}>
                  <Input
                    placeholder="Parameter Name"
                    value={param.name || ''}
                    onChange={(e) => handleToolParameterChange(index, 'name', e.target.value)}
                    style={{ flex: 1 }}
                    className="nodrag"
                  />
                  <Switch
                    checked={param.required !== false}
                    onChange={(checked) => handleToolParameterChange(index, 'required', checked)}
                    className="nodrag"
                  />
                  <Text style={{ lineHeight: '32px', minWidth: '60px' }}>Required</Text>
                  <Button 
                    icon={<MinusCircleOutlined />}
                    onClick={() => handleRemoveToolParameter(index)}
                    danger
                  />
                </div>
                
                <div style={{ marginBottom: 8 }}>
                  <TextArea
                    placeholder="Parameter Value"
                    value={param.value || ''}
                    onChange={(e) => handleToolParameterChange(index, 'value', e.target.value)}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    className="nodrag"
                  />
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <NodeInputHandler
                    nodeId={id}
                    onInsert={(tag) => handleToolParameterChange(index, 'value', (param.value || '') + tag)}
                  />
                  <Input
                    placeholder="Description (optional)"
                    value={param.description || ''}
                    onChange={(e) => handleToolParameterChange(index, 'description', e.target.value)}
                    size="small"
                    style={{ width: '200px' }}
                    className="nodrag"
                  />
                </div>
              </div>
            ))}

            <Button 
              type="dashed"
              onClick={handleAddToolParameter}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              Add Parameter
            </Button>
          </>
        )}
      </Space>
    );
  };

  // Render test results
  const renderTestResults = () => {
    if (isTesting) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin tip="Testing MCP connection..." />
        </div>
      );
    }

    if (testError) {
      return (
        <Alert
          type="error"
          message="Test Failed"
          description={testError}
          style={{ marginBottom: 16 }}
        />
      );
    }

    if (testResult) {
      return (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Status:</Text>{' '}
            <Tag color={testResult.success ? 'success' : 'error'}>
              {testResult.success ? 'Success' : 'Failed'}
            </Tag>
          </div>

          <Text strong>Response:</Text>
          <div style={{ position: 'relative' }}>
            <TextArea
              value={JSON.stringify(testResult.data || testResult, null, 2)}
              autoSize={{ minRows: 4, maxRows: 10 }}
              readOnly
              style={{ marginTop: 8 }}
            />
            <Button
              icon={<CopyOutlined />}
              style={{ position: 'absolute', right: 8, top: 8 }}
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(testResult.data || testResult, null, 2));
                message.success('Response copied to clipboard');
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <Alert
        type="info"
        message="No test results yet"
        description="Configure your MCP server and tool, then click 'Test Connection' to see results here."
        style={{ marginBottom: 16 }}
      />
    );
  };

  return (
    <Card
      title={
        <Space>
          <ApiOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
          <span>MCP Client</span>
          {serverType !== 'custom' && (
            <Tag color="blue" icon={currentServer.icon}>
              {currentServer.name}
            </Tag>
          )}
        </Space>
      }
      style={{ width: 420 }}
      extra={
        <Space>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={testConnection}
            loading={isTesting}
            disabled={!selectedTool || !serverType}
          >
            Test
          </Button>
          <Popconfirm
            title="Delete Node"
            description="Are you sure you want to delete this node?"
            onConfirm={() => data.onDelete(id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      }
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      
      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={(e) => setNodeName(e.target.value)}
        style={{ marginBottom: 16 }}
        prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
        className="nodrag"
      />

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Server" key="server">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>Server Type</Text>
            <Select
              value={serverType}
              onChange={handleServerTypeChange}
              style={{ width: '100%' }}
              className="nodrag"
            >
              <OptGroup label="Popular Services">
                {Object.entries(PRESET_SERVERS).map(([key, server]) => (
                  <Option key={key} value={key}>
                    <Space>
                      {server.icon}
                      <span>{server.name}</span>
                    </Space>
                  </Option>
                ))}
              </OptGroup>
              <OptGroup label="Custom">
                <Option value="custom">
                  <Space>
                    <SettingOutlined />
                    <span>Custom MCP Server</span>
                  </Space>
                </Option>
              </OptGroup>
            </Select>

            <Alert
              type="info"
              message={currentServer.name}
              description={currentServer.description}
              style={{ marginTop: 8 }}
            />

            {renderCustomServerConfig()}

            <Divider />
            {renderToolConfiguration()}
          </Space>
        </TabPane>

        <TabPane tab="Authentication" key="auth">
          {renderAuthFields()}
        </TabPane>

        <TabPane tab="Test" key="test">
          {renderTestResults()}
        </TabPane>
      </Tabs>

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
    </Card>
  );
};

export default MCPClientNode;